import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { fmtDate, today, fmtINR } from "@/lib/format";
import { Plus, ArrowRight, CheckCircle2, XCircle, Send, Zap, Trash2 } from "lucide-react";
import { useSubscription } from "@/context/SubscriptionContext";

const STAGES = ["idea", "feasibility", "design", "prototype", "pilot", "launch", "close"];

function ProjectsTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const empty = { code: "", name: "", description: "", target_launch_date: "", target_cost: 0, priority: "normal" };
  const [form, setForm] = useState(empty);
  const load = async () => setRows((await api.get("/npd/projects")).data);
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.code || !form.name) return toast.error("Code + Name required");
    try { await api.post("/npd/projects", { ...form, target_launch_date: form.target_launch_date || null }); toast.success("Created"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const advance = async (p) => {
    const notes = window.prompt("Gate notes (optional):", "") || "";
    try { await api.post(`/npd/projects/${p.id}/advance`, { gate_notes: notes }); toast.success("Advanced"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete project?")) return; await api.delete(`/npd/projects/${id}`); load(); };

  const stageIdx = (s) => STAGES.indexOf(s);
  const stageColor = (s) => {
    const idx = stageIdx(s);
    return idx <= 1 ? "text-blue-500" : idx <= 3 ? "text-yellow-500" : idx <= 5 ? "text-orange-500" : "text-emerald-600";
  };
  return (
    <div className="space-y-3" data-testid="npd-projects-tab">
      <div className="flex justify-end"><Button onClick={() => { setForm(empty); setOpen(true); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-npd-btn"><Plus className="w-3.5 h-3.5" /> NPD Project</Button></div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Code</th><th>Name</th><th>Stage</th><th>Priority</th><th>Target launch</th><th className="text-right">Target ₹</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-muted-foreground">No NPD projects yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`npd-row-${r.code}`}>
                <td className="font-mono text-xs">{r.code}</td>
                <td className="font-medium">{r.name}</td>
                <td>
                  <div className="flex items-center gap-1">
                    <span className={`text-[10px] font-mono uppercase ${stageColor(r.current_stage)}`}>{r.current_stage}</span>
                    <div className="flex gap-0.5 ml-2">
                      {STAGES.slice(0, -1).map((s, i) => (
                        <div key={s} className={`w-2 h-1.5 rounded-sm ${i < stageIdx(r.current_stage) ? "bg-emerald-500" : i === stageIdx(r.current_stage) ? "bg-yellow-500" : "bg-muted"}`} title={s} />
                      ))}
                    </div>
                  </div>
                </td>
                <td><span className={`text-[10px] font-mono uppercase ${r.priority === "urgent" ? "text-red-500" : r.priority === "high" ? "text-orange-500" : "text-muted-foreground"}`}>{r.priority}</span></td>
                <td className="text-xs">{r.target_launch_date ? fmtDate(r.target_launch_date) : "—"}</td>
                <td className="text-right tabular-nums">{fmtINR(r.target_cost)}</td>
                <td><span className="text-[10px] font-mono uppercase text-accent">{r.status}</span></td>
                <td className="text-right">
                  {r.current_stage !== "close" && r.status === "active" && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => advance(r)} data-testid={`advance-${r.code}`}><ArrowRight className="w-3 h-3" /> Advance</Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setDetail(r)}>View</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-red-500" onClick={() => del(r.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg rounded-none space-y-3 overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading">New NPD Project</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" data-testid="npd-code" /></div>
            <div><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "normal", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" data-testid="npd-name" /></div>
            <div className="col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>Target launch</Label><Input type="date" value={form.target_launch_date} onChange={(e) => setForm({ ...form, target_launch_date: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>Target cost ₹</Label><Input type="number" value={form.target_cost} onChange={(e) => setForm({ ...form, target_cost: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
          </div>
          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="npd-save">Save</Button>
        </SheetContent>
      </Sheet>

      {/* Detail: stage history */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3 overflow-y-auto">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-heading">{detail.code} · {detail.name}</SheetTitle></SheetHeader>
              <div className="text-sm text-muted-foreground">{detail.description}</div>
              <div className="text-[10px] font-mono uppercase text-muted-foreground mt-3">Stage history</div>
              <div className="space-y-2">
                {(detail.stage_history || []).length === 0 && <div className="text-xs text-muted-foreground">Fresh — currently at {detail.current_stage}.</div>}
                {(detail.stage_history || []).map((h, i) => (
                  <div key={i} className="border-l-2 border-emerald-500 pl-3 py-1">
                    <div className="text-[10px] font-mono uppercase text-emerald-600">{h.stage}</div>
                    <div className="text-xs text-muted-foreground">{h.completed_at ? fmtDate(h.completed_at.slice(0,10)) : ""}</div>
                    {h.gate_notes && <div className="text-xs italic mt-1">"{h.gate_notes}"</div>}
                  </div>
                ))}
                <div className="border-l-2 border-yellow-500 pl-3 py-1">
                  <div className="text-[10px] font-mono uppercase text-yellow-600">{detail.current_stage} (current)</div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ECTab() {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [boms, setBoms] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { title: "", item_id: "", change_type: "request", reason: "quality", description: "",
                   from_bom_id: "", to_bom_id: "", effective_date: today(), priority: "normal" };
  const [form, setForm] = useState(empty);
  const load = async () => {
    setRows((await api.get("/npd/engineering-changes")).data);
    setItems((await api.get("/items")).data);
    setBoms((await api.get("/bom")).data);
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.title || !form.item_id) return toast.error("Title + Item required");
    try { await api.post("/npd/engineering-changes", { ...form,
                          from_bom_id: form.from_bom_id || null,
                          to_bom_id: form.to_bom_id || null,
                          effective_date: form.effective_date || null }); toast.success("Created"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const action = async (ec, act) => { try { await api.post(`/npd/engineering-changes/${ec.id}/${act}`); toast.success(act); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const statusColor = (s) => s === "approved" ? "text-emerald-600" : s === "implemented" ? "text-emerald-700" : s === "rejected" ? "text-red-500" : s === "submitted" ? "text-yellow-500" : "text-muted-foreground";
  return (
    <div className="space-y-3" data-testid="ec-tab">
      <div className="flex justify-end"><Button onClick={() => { setForm(empty); setOpen(true); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-ec-btn"><Plus className="w-3.5 h-3.5" /> ECR</Button></div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>#</th><th>Title</th><th>Item</th><th>Type</th><th>Reason</th><th>Effective</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-muted-foreground">No engineering changes yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`ec-row-${r.change_number}`}>
                <td className="font-mono text-xs">{r.change_number}</td>
                <td className="font-medium">{r.title}</td>
                <td>{r.item_name}<div className="text-[10px] font-mono text-muted-foreground">{r.item_sku}</div></td>
                <td><span className={`text-[10px] font-mono uppercase ${r.change_type === "order" ? "text-accent" : "text-muted-foreground"}`}>{r.change_type}</span></td>
                <td className="text-xs">{r.reason}</td>
                <td className="text-xs">{r.effective_date ? fmtDate(r.effective_date) : "—"}</td>
                <td><span className={`text-[10px] font-mono uppercase ${statusColor(r.status)}`}>{r.status}</span></td>
                <td className="text-right">
                  {r.status === "draft" && <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => action(r, "submit")} data-testid={`submit-ec-${r.change_number}`}><Send className="w-3 h-3" /> Submit</Button>}
                  {r.status === "submitted" && <>
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-emerald-600" onClick={() => action(r, "approve")}><CheckCircle2 className="w-3 h-3" /> Approve</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => action(r, "reject")}><XCircle className="w-3 h-3" /></Button>
                  </>}
                  {r.status === "approved" && <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-emerald-700" onClick={() => action(r, "implement")} data-testid={`implement-ec-${r.change_number}`}><Zap className="w-3 h-3" /> Implement</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg rounded-none space-y-3 overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading">New ECR (Engineering Change Request)</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm mt-1" data-testid="ec-title" /></div>
            <div className="col-span-2"><Label>Item</Label>
              <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="ec-item"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="max-h-72"><div className="max-h-72 overflow-y-auto">{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</div></SelectContent>
              </Select>
            </div>
            <div><Label>Reason</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{["cost_reduction", "quality", "regulatory", "design_improvement", "supplier_change", "other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "normal", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>From BOM (old)</Label>
              <Select value={form.from_bom_id} onValueChange={(v) => setForm({ ...form, from_bom_id: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="(none)" /></SelectTrigger>
                <SelectContent>{boms.filter((b) => !form.item_id || b.parent_item_id === form.item_id).map((b) => <SelectItem key={b.id} value={b.id}>{b.code || b.id.slice(0, 6)} · {b.version}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>To BOM (new)</Label>
              <Select value={form.to_bom_id} onValueChange={(v) => setForm({ ...form, to_bom_id: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="(none)" /></SelectTrigger>
                <SelectContent>{boms.filter((b) => !form.item_id || b.parent_item_id === form.item_id).map((b) => <SelectItem key={b.id} value={b.id}>{b.code || b.id.slice(0, 6)} · {b.version}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Effective date</Label><Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} className="rounded-sm mt-1" /></div>
            <div className="col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1" /></div>
          </div>
          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="ec-save">Save</Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default function NPD() {
  const { hasModule } = useSubscription();
  if (!hasModule("npd")) return <div className="p-8"><div className="card-sharp p-10 text-center"><div className="font-heading text-2xl">NPD & ECO/ECR require Enterprise Manufacturing</div><a href="/app/subscription" className="inline-block mt-4 px-5 py-2 bg-accent text-white rounded-sm text-sm">Upgrade →</a></div></div>;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="npd-page">
      <PageHeader eyebrow="Manufacturing" title="NPD · Engineering Change"
        description="7-stage NPD gate model (idea → feasibility → design → prototype → pilot → launch → close). ECR/ECO change management with BOM re-effective dating on implementation." />
      <Tabs defaultValue="projects">
        <TabsList className="rounded-sm">
          <TabsTrigger value="projects" className="rounded-sm" data-testid="tab-npd-projects">Projects</TabsTrigger>
          <TabsTrigger value="ec" className="rounded-sm" data-testid="tab-ec">Engineering Changes</TabsTrigger>
        </TabsList>
        <TabsContent value="projects" className="mt-4"><ProjectsTab /></TabsContent>
        <TabsContent value="ec" className="mt-4"><ECTab /></TabsContent>
      </Tabs>
    </div>
  );
}
